import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  MenuItem,
  Stack,
} from "@mui/material";
import AddNewSessionDialog from "app/components/common/Dialogs/school/AddNewSessionDialog";
import OrderDetailDialog from "app/components/common/Dialogs/school/OrderDetailDialog";
import RazorPayPaymentDialog from "app/components/common/Dialogs/school/RazorPayPaymentDialog";
import useRazorPayPaymentGate from "app/components/common/Dialogs/school/RazorPayPaymentGate";
import TransactionDetailDialog from "app/components/common/Dialogs/school/TransactionDetail";
import { getAllSessions } from "app/services/management";
import { getAllSessionOrderBySchool } from "app/services/orgService/school.service";
import { MenuProps } from "app/utils/helper";
import { CustomSelectBox } from "assets/styles/globalStyledComponent";
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  selectActiveSession,
  selectSelectedSchool,
  selectSelectedSchoolId,
} from "../../selectors";
import CurrentSubscriptionDetailsCard from "./components/transactions/CurrentSubscriptionDetailsCard";
import OtpPlanAcceptance from "./components/transactions/OtpPlanAcceptance";
import TransactionTable from "./components/transactions/TransactionTable";
import ActiveAddons from "./components/transactions/ActiveAddons";
import TransactionSummary from "./components/transactions/TransactionSummary";
import { Add } from "@mui/icons-material";

const Transaction = () => {
  const activeSession = useSelector(selectActiveSession);
  const schoolDetails = useSelector(selectSelectedSchool);
  const selectedSchoolId = useSelector(selectSelectedSchoolId);

  const [state, setState] = useState({
    addSessionDialog: false,
    session: "",
    data: [],
    summary: {},
    schoolInfo: {},
    sessionInfo: {},
    orderDetailDialog: false,
    orderDetailData: {},
    transactionDetailDialog: false,
    collectPaymentDialog: false,
    sessionOrderDocId: "",
    transactionList: [],
    purchaseOrderDetails: {},
  });

  const calculateTobePaid = useMemo(() => {
    const filteredList = state.data?.filter((item) => item?.checked) ?? [];
    return filteredList.reduce((total, currentValue) => {
      const tobePaid =
        Number(currentValue?.netOrderAmount ?? 0) -
        Number(currentValue?.paidAmount ?? 0) -
        Number(currentValue?.totalExtraDiscount ?? 0);
      return total + tobePaid;
    }, 0);
  }, [state.data]);

  const activeAddOnOrders = useMemo(
    () =>
      (state.data || []).filter(
        (item) => item?.productDetail?.productName === "Add_on"
      ),
    [state.data]
  );

  const {
    isLoading: getAllSessionOrderBySchoolLoading,
    isFetching: getAllSessionOrderBySchoolFetching,
    refetch: getAllSessionOrderBySchoolRefetch,
  } = useQuery({
    queryKey: ["getAllSessionOrderBySchool", state.session],
    queryFn: () => getAllSessionOrderBySchool({ session: state.session }),
    onSuccess: (success) => {
      setState((prev) => ({
        ...prev,
        data: success?.data?.list,
        schoolInfo: success?.data?.schoolInfo,
        sessionInfo: success?.data?.sessionInfo,
        summary: success?.data?.summary,
      }));
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    },
    enabled: !!state.session,
  });

  const { data: sessionsResponse } = useQuery({
    queryKey: ["getAllSessions"],
    queryFn: () => getAllSessions(),
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    },
  });

  useEffect(() => {
    setState((prev) => ({ ...prev, session: activeSession }));
  }, [activeSession]);

  const isAnyItemChecked = useMemo(
    () => state.data.some((item) => item.checked),
    [state.data]
  );

  const { openRazorpay } = useRazorPayPaymentGate({
    refetch: getAllSessionOrderBySchoolRefetch,
  });

  return (
    <>
      <Helmet>
        <title>Transaction History</title>
      </Helmet>

      {/* Dialogs */}
      <RazorPayPaymentDialog
        open={state.collectPaymentDialog}
        onClose={() =>
          setState((prev) => ({ ...prev, collectPaymentDialog: false }))
        }
        data={state.data?.filter((item) => item?.checked) || []}
        schoolDocId={schoolDetails?._id}
        session={state.session}
        refetch={getAllSessionOrderBySchoolRefetch}
      />

      <TransactionDetailDialog
        open={state.transactionDetailDialog}
        onClose={() =>
          setState((prev) => ({ ...prev, transactionDetailDialog: false }))
        }
        id={state.sessionOrderDocId}
        transactionList={state.transactionList}
      />

      <OrderDetailDialog
        open={state.orderDetailDialog}
        onClose={() =>
          setState((prev) => ({
            ...prev,
            orderDetailDialog: false,
            orderDetailData: {},
          }))
        }
        data={state.orderDetailData}
      />

      <AddNewSessionDialog
        open={state.addSessionDialog}
        onClose={() =>
          setState((prev) => ({ ...prev, addSessionDialog: false }))
        }
        schoolDocId={selectedSchoolId}
        handlePayementGateWay={(sucessData) => {
          openRazorpay(sucessData);
        }}
      />

      <Box>
        {/* Header Section */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
          mb={2}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
          >
            <CustomSelectBox
              MenuProps={MenuProps}
              size="medium"
              sx={{ minWidth: 200, borderRadius: 2 }}
              displayEmpty
              value={state.session}
            >
              <MenuItem value="" disabled>
                Select Session
              </MenuItem>
              {sessionsResponse?.data?.list?.map((item, idx) => (
                <MenuItem
                  value={item?.sessionName}
                  key={idx}
                  onClick={() =>
                    setState((prev) => ({
                      ...prev,
                      session: item?.sessionName,
                    }))
                  }
                >
                  {item?.sessionName} (Limit: {item?.studentLimitCount})
                </MenuItem>
              ))}
            </CustomSelectBox>

            <LoadingButton
              loading={
                getAllSessionOrderBySchoolLoading ||
                getAllSessionOrderBySchoolFetching
              }
              variant="contained"
              color="primary"
              onClick={() => getAllSessionOrderBySchoolRefetch()}
              disabled={!state.session}
              size="large"
            >
              View
            </LoadingButton>
          </Stack>

          <Button
            variant="contained"
            color="secondary"
            onClick={() =>
              setState((prev) => ({ ...prev, addSessionDialog: true }))
            }
            size="large"
            startIcon={<Add />}
          >
            New Session
          </Button>
        </Stack>

        <CurrentSubscriptionDetailsCard  schoolInfo={state.schoolInfo} sessionInfo={state.sessionInfo} session={state.session} />

        <OtpPlanAcceptance
          session={state.session}
          sessionInfo={state.sessionInfo}
          schoolInfo={state.schoolInfo}
          refetch={getAllSessionOrderBySchoolRefetch}
        />

        {/* Table Section */}
        <TransactionTable
          data={state.data}
          onCheckItem={(index) =>
            setState((prev) => ({
              ...prev,
              data: prev.data.map((item, i) =>
                i === index ? { ...item, checked: !item.checked } : item
              ),
            }))
          }
          onOrderDetailClick={(item) =>
            setState((prev) => ({
              ...prev,
              orderDetailData: item,
              orderDetailDialog: true,
            }))
          }
          onTransactionDetailClick={(item) =>
            setState((prev) => ({
              ...prev,
              sessionOrderDocId: item?._id,
              transactionDetailDialog: true,
              transactionList: item?.transactions,
            }))
          }
        />

        {/* Active Add-ons Section */}
        {state.session && (
          <ActiveAddons
            session={state.session}
            activeAddOnOrders={activeAddOnOrders}
          />
        )}

        {/* Summary & Actions */}
        <TransactionSummary
          summary={state.summary}
          calculateTobePaid={calculateTobePaid}
          isAnyItemChecked={isAnyItemChecked}
          onPayNow={() =>
            setState((prev) => ({ ...prev, collectPaymentDialog: true }))
          }
        />
      </Box>
    </>
  );
};

export default Transaction;
